import React from 'react';

import styled from '@emotion/styled';
import Img from '../Img/Img';
import JobsIcon from '../Icons/JobsIcon';
import { ToggleButton } from '../ToggleButton/ToggleButton';
import {
  extractTextFromHTML,
  salaryInterval,
  truncateText,
  truncateTextByCharacters,
} from '../../utils/utils';
import useJobPermissions from '../../hooks/jobs/useJobPermissions';
import { useAppSelector } from '../../redux/store';
import { ModalProvider } from '../../context/ModalContext';
import ModalTriggerContainer from '../Modal/ModalTriggerContainer';
import { Modal } from '../../components/Modal/Modal';
import PostJobModal from '../Modal/ModalActions/PostJobModal';

interface IJobsCard {
  jobId: string;
  imgSrc?: string | null;
  institutionName: string;
  description: string;
  xp: string;
  pay: number;
  payInterval: string;
}

const JobsCard: React.FC<IJobsCard> = ({
  imgSrc,
  institutionName,
  description,
  xp,
  pay,
  jobId,
  payInterval,
}) => {
  useJobPermissions(jobId, true);

  // Use your Redux selector to find the permission for this job.
  const jobPermission = useAppSelector((state) =>
    state.jobPermissions.permissions.find((perm) => perm.jobId === jobId)
  );

  const EditJobButton = ({ children }: { children: React.ReactNode }) => {
    return (
      <ModalProvider>
        <ModalTriggerContainer className="full right">
          {children}
        </ModalTriggerContainer>
        <Modal>
          <PostJobModal jobId={jobId} />
        </Modal>
      </ModalProvider>
    );
  };

  return (
    <Container>
      <JobCardHeader>
        <ImageContainer>
          <Img
            src={imgSrc ?? '/static/img/company-placeholder.png'}
            alt="shift location image"
          />
        </ImageContainer>
        <JobCardHeaderText>{institutionName}</JobCardHeaderText>
        <IconContainer>
          <ToggleButton
            controller={() => console.log('Toggle')}
            defaultState={false}
          />
        </IconContainer>
      </JobCardHeader>
      <JobCardDescription>
        {(() => {
          const text = extractTextFromHTML(description);
          const first20 = text.slice(0, 20);
          const spaceCount = (first20.match(/ /g) || []).length;
          return spaceCount < 4
            ? truncateTextByCharacters(text, 40)
            : truncateText(text, 9);
        })()}
      </JobCardDescription>
      {/* <ButtonLink to={link}>
        <PrimaryButton arrowIcon className="noBorder">
          Apply
        </PrimaryButton>
      </ButtonLink> */}
      <JobCardFooter>
        <Xp>
          <JobsIcon />
          <span>{xp}</span>years+
        </Xp>
        <Pay>
          {' '}
          {pay.toLocaleString('en-NG', {
            style: 'currency',
            currency: 'NGN',
          })}
          /{salaryInterval[payInterval]}
        </Pay>
        <EditJobButton>
          <Edit disabled={!jobPermission?.canEdit}>Edit</Edit>
        </EditJobButton>
      </JobCardFooter>
    </Container>
  );
};

export default JobsCard;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 0.3125rem;
  padding: 1rem;
  width: 22.6rem;
  height: 11rem;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ImageContainer = styled.div`
  width: 3.125rem;
  height: 3.125rem;
`;

const JobCardHeader = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.81rem;
`;

const JobCardHeaderText = styled.div`
  font-size: 20px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.blackBlack2};
`;

const JobCardDescription = styled.div`
  font-weight: 300;
  color: ${({ theme }) => theme.palette.greyGrey1};
  max-width: 270px;
`;

const IconContainer = styled.div`
  width: 2rem;
  margin-left: auto;
  cursor: pointer;
  height: 100%;
`;

const JobCardFooter = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 2rem;
  font-size: 14px;
  font-weight: 400;
`;

const Xp = styled.p`
  display: flex;
  justfy-content: center;
  align-items: center;
  gap: 0.2rem;

  & > svg {
    width: 14px;
  }
`;

const Pay = styled.div``;

const Edit = styled.button`
  background: none;
  border: none;
  margin-left: auto;
  color: ${({ theme }) => theme.palette.mainBlue};
  font-weight: 400;
  font-size: 18px;
  line-height: 100%;
  cursor: pointer;

  &:disabled {
    color: ${({ theme }) => theme.palette.greyGrey1};
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
